/* eslint-disable react-hooks/rules-of-hooks */
import { useRef, useState, useEffect } from "react";
import {
  motion, useScroll, useTransform, useSpring,
  useMotionValue, AnimatePresence,
} from "framer-motion";

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const SPRING     = { stiffness: 100, damping: 30, restDelta: 0.001 };
const SPRING_FAST = { stiffness: 180, damping: 22 };
const serif = "'Playfair Display', Georgia, serif";
const sans  = "'Cormorant Garamond', Georgia, serif";

/* ─────────────────────────────────────────────
   GOOGLE FONTS
───────────────────────────────────────────── */
const useFonts = () => {
  useEffect(() => {
    const el = document.createElement("link");
    el.rel  = "stylesheet";
    el.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Cormorant+Garamond:wght@300;400;600&display=swap";
    document.head.appendChild(el);
  }, []);
};

/* ─────────────────────────────────────────────
   BLOOD GRAIN
───────────────────────────────────────────── */
const BloodGrain = () => (
  <svg className="fixed inset-0 w-full h-full pointer-events-none z-[60] opacity-[0.055]"
    style={{ mixBlendMode: "color-dodge" }} aria-hidden="true">
    <defs>
      <filter id="bg" x="0%" y="0%" width="100%" height="100%">
        <feTurbulence type="fractalNoise" baseFrequency="0.72" numOctaves="4" seed="2" stitchTiles="stitch" result="n"/>
        <feColorMatrix type="matrix"
          values="1 0 0 0 0.4  0 0 0 0 0  0 0 0 0 0  0 0 0 18 -7" in="n" result="r"/>
        <feComposite in="r" in2="SourceGraphic" operator="in"/>
      </filter>
    </defs>
    <rect width="100%" height="100%" filter="url(#bg)"/>
  </svg>
);

/* ─────────────────────────────────────────────
   RED CURSOR + TRAIL
───────────────────────────────────────────── */
const RedCursor = () => {
  const cx = useMotionValue(-200), cy = useMotionValue(-200);
  const sx = useSpring(cx, { stiffness: 90, damping: 18 });
  const sy = useSpring(cy, { stiffness: 90, damping: 18 });
  const trailMVs = useRef(
    [...Array(6)].map(() => ({ x: useMotionValue(-200), y: useMotionValue(-200) }))
  );
  const pos = useRef({ x: -200, y: -200 });
  const [hov, setHov] = useState(false);

  useEffect(() => {
    const mv = (e) => { pos.current = { x: e.clientX, y: e.clientY }; cx.set(e.clientX - 12); cy.set(e.clientY - 12); };
    const mo = (e) => setHov(!!e.target.closest("a,button,[data-mag]"));
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseover", mo);
    let raf;
    const lerp = (a, b, t) => a + (b - a) * t;
    const tick = () => {
      const { x, y } = pos.current;
      trailMVs.current.forEach((d, i) => {
        const t = 0.08 + i * 0.04;
        d.x.set(lerp(d.x.get(), x - 4, t));
        d.y.set(lerp(d.y.get(), y - 4, t));
      });
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseover", mo); cancelAnimationFrame(raf); };
  }, []);

  return (
    <>
      {trailMVs.current.map((d, i) => (
        <motion.div key={i}
          style={{ left: d.x, top: d.y, opacity: (6 - i) / 10, scale: 1 - i * 0.13 }}
          className="fixed w-2 h-2 rounded-full bg-red-600/40 pointer-events-none z-[998]" />
      ))}
      <motion.div
        style={{ left: sx, top: sy, scale: hov ? 1.9 : 1 }}
        transition={SPRING}
        className="fixed w-6 h-6 rounded-full pointer-events-none z-[999] border border-red-500"
      >
        <div className="absolute inset-0 rounded-full bg-red-600/20 blur-sm" />
        <div className="absolute inset-[5px] rounded-full bg-red-500/60 blur-[2px]" />
      </motion.div>
    </>
  );
};

/* ─────────────────────────────────────────────
   NAV
───────────────────────────────────────────── */
const Nav = ({ onMenu }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <motion.nav
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.4, ...SPRING }}
      className="fixed top-0 inset-x-0 z-[100] flex justify-between items-center px-8 md:px-14 py-7 transition-all duration-500"
      style={{ backdropFilter: scrolled ? "blur(14px)" : "none", background: scrolled ? "rgba(0,0,0,0.65)" : "transparent" }}
    >
      <span style={{ fontFamily: serif }} className="text-white text-xl tracking-[0.4em] uppercase font-black">GUAN</span>
      <div className="hidden md:flex gap-10 text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: sans }}>
        {["The Rooftop", "Events", "Reserve"].map(l => (
          <motion.a key={l} href="#" data-mag
            whileHover={{ color: "#ef4444", letterSpacing: "0.45em" }} transition={SPRING}
            className="text-white/50 transition-all">{l}</motion.a>
        ))}
      </div>
      <button onClick={onMenu} data-mag
        className="md:hidden text-white/60 text-[10px] tracking-[0.35em] uppercase border border-white/10 px-4 py-2 hover:border-red-700/60 transition-colors"
        style={{ fontFamily: sans }}>MENU</button>
    </motion.nav>
  );
};

/* ─────────────────────────────────────────────
   MOBILE MENU — blood wipe
───────────────────────────────────────────── */
const MobileMenu = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div
          initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} exit={{ scaleY: 0 }}
          transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
          style={{ originY: 0 }}
          className="fixed inset-0 z-[190] bg-red-950"
        />
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ delay: 0.3 }}
          className="fixed inset-0 z-[200] flex flex-col justify-end pb-20 px-10"
        >
          <button onClick={onClose}
            className="absolute top-8 right-8 text-white/40 text-[10px] tracking-[0.5em] uppercase">
            × Close
          </button>
          {["The Rooftop", "The View", "Events", "Reserve"].map((item, i) => (
            <motion.a key={item} href="#"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.07, ...SPRING }}
              onClick={onClose}
              style={{ fontFamily: serif }}
              className="block text-5xl text-white/90 tracking-tight border-b border-white/5 py-4 hover:text-red-400 transition-colors"
            >{item}</motion.a>
          ))}
          <p className="mt-10 text-red-900/70 text-[10px] tracking-[0.5em] uppercase" style={{ fontFamily: sans }}>
            Floreasca · Bucharest · Level 12
          </p>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

/* ─────────────────────────────────────────────
   SPLIT LINE ANIMATOR
───────────────────────────────────────────── */
const SplitLines = ({ lines, className, stroke }) => (
  <div>
    {lines.map((line, li) => (
      <div key={li} className="overflow-hidden">
        <motion.div
          initial="hidden" whileInView="visible"
          viewport={{ once: true, margin: "-8%" }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.022, delayChildren: li * 0.1 } },
          }}
          className={`flex flex-wrap ${className}`}
        >
          {line.split("").map((ch, ci) => (
            <motion.span key={ci}
              variants={{
                hidden: { y: "110%", opacity: 0, skewY: 7 },
                visible: { y: "0%", opacity: 1, skewY: 0, transition: SPRING },
              }}
              style={stroke
                ? { WebkitTextStroke: "1px #991b1b", color: "transparent", display: "inline-block" }
                : { display: "inline-block" }
              }
            >{ch === " " ? "\u00A0" : ch}</motion.span>
          ))}
        </motion.div>
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   HERO — GUAN scales to 5× as portal/mask
───────────────────────────────────────────── */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const rawScale   = useTransform(scrollYProgress, [0, 1], [1, 5]);
  const titleScale = useSpring(rawScale, SPRING);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.55, 0.85], [1, 1, 0]);
  const bgScale    = useSpring(useTransform(scrollYProgress, [0, 1], [1, 1.18]), SPRING);
  const overlayOp  = useTransform(scrollYProgress, [0.35, 0.9], [0, 1]);

  const [loaded, setLoaded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setLoaded(true), 280); return () => clearTimeout(t); }, []);

  return (
    <section ref={ref} style={{ height: "220vh" }} className="relative">
      <div className="sticky top-0 h-screen overflow-hidden bg-black flex items-center justify-center">

        {/* BG */}
        <motion.div style={{ scale: bgScale }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-red-950/50 to-black" />
          {/* Pulsing red haze — screen blend */}
          <motion.div
            animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.06, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[-10%] right-[-5%] w-[80vw] h-[80vh] rounded-full"
            style={{ background: "radial-gradient(ellipse, #7f1d1d55 0%, transparent 70%)", mixBlendMode: "screen" }}
          />
          <motion.div
            animate={{ opacity: [0.3, 0.65, 0.3], x: [-30, 30, -30] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-[10%] left-[-10%] w-[60vw] h-[50vh] rounded-full"
            style={{ background: "radial-gradient(ellipse, #991b1b40 0%, transparent 70%)", mixBlendMode: "color-dodge" }}
          />
          {/* Drone placeholder */}
          <div className="absolute inset-0 flex flex-col items-center justify-center opacity-10">
            <div className="w-[70vw] h-[50vh] border border-red-900/30 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-px bg-red-800/60 mx-auto mb-3" />
                <p className="text-red-700/70 text-[9px] tracking-[0.5em] uppercase" style={{ fontFamily: sans }}>
                  Cinematic Drone · Floreasca Skyline<br />22:14 · Golden Hour Descending
                </p>
                <div className="w-16 h-px bg-red-800/60 mx-auto mt-3" />
              </div>
            </div>
          </div>
          {/* Scan-lines */}
          <div className="absolute inset-0 opacity-[0.035]"
            style={{ backgroundImage: "repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 3px)" }} />
        </motion.div>

        {/* GUAN title — scales up like a portal */}
        <motion.div
          style={{ scale: titleScale, opacity: titleOpacity }}
          className="relative z-10 text-center select-none pointer-events-none"
        >
          <AnimatePresence>
            {loaded && (
              <>
                <motion.p key="eyebrow"
                  initial={{ opacity: 0, letterSpacing: "0.2em" }}
                  animate={{ opacity: 1, letterSpacing: "0.6em" }}
                  transition={{ delay: 0.4, duration: 1.2 }}
                  className="text-red-700/80 text-[10px] uppercase mb-6 tracking-[0.6em]"
                  style={{ fontFamily: sans }}
                >Bucharest · Level 12 · Est. 2024</motion.p>
                <motion.h1 key="guan"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, ...SPRING }}
                  className="font-black text-white leading-none tracking-tighter"
                  style={{
                    fontFamily: serif,
                    fontSize: "clamp(7rem, 22vw, 18rem)",
                    letterSpacing: "-0.04em",
                    mixBlendMode: "difference",
                  }}
                >GUAN</motion.h1>
                <motion.div key="rule"
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                  transition={{ delay: 1.0, duration: 0.9, ease: "easeOut" }}
                  className="w-20 h-px bg-red-700 mx-auto mt-8"
                />
                <motion.p key="sub"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ delay: 1.3, duration: 0.8 }}
                  className="text-white/25 text-[10px] tracking-[0.5em] uppercase mt-5"
                  style={{ fontFamily: sans }}
                >Rooftop · Bar · Lounge</motion.p>
              </>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Vignette deepens as you scroll */}
        <motion.div style={{ opacity: overlayOp }} className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, transparent 30%, #000 100%)", opacity: overlayOp }} />

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-20"
        >
          <motion.div
            animate={{ scaleY: [0, 1, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            style={{ originY: 0 }}
            className="w-px h-12 bg-gradient-to-b from-red-600 to-transparent"
          />
          <p className="text-white/20 text-[9px] tracking-[0.6em] uppercase" style={{ fontFamily: sans }}>Descend</p>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   MANIFESTO — split chars + image reveal
───────────────────────────────────────────── */
const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const paraX    = useSpring(useTransform(scrollYProgress, [0, 1], [0, 60]), SPRING);
  const imgY     = useSpring(useTransform(scrollYProgress, [0, 1], [-40, 40]), SPRING);
  const imgScale = useSpring(useTransform(scrollYProgress, [0, 0.5], [1.15, 1]), SPRING);
  const imgGray  = useTransform(scrollYProgress, [0, 0.45], [1, 0]);
  const imgBright = useTransform(scrollYProgress, [0, 0.45], [0.5, 1]);

  return (
    <section ref={ref} className="relative bg-black py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 20% 50%, #7f1d1d18 0%, transparent 65%)", mixBlendMode: "screen" }} />

      <div className="relative z-10 px-8 md:px-16 grid md:grid-cols-2 gap-12 items-center">
        {/* TEXT */}
        <div className="order-2 md:order-1">
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-red-700/70 text-[10px] tracking-[0.55em] uppercase mb-8"
            style={{ fontFamily: sans }}
          >§ 01 — Philosophy</motion.p>

          <div style={{ fontFamily: serif, fontSize: "clamp(3rem, 7vw, 6rem)", lineHeight: 0.95, fontWeight: 900 }}>
            <SplitLines lines={["Above", "the City"]} className="text-white" />
            <div className="mt-1">
              <SplitLines lines={["Below", "the Stars"]} stroke />
            </div>
          </div>

          <motion.div style={{ x: paraX }} className="mt-10 max-w-sm">
            <motion.p
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.5, ...SPRING }}
              className="text-white/40 text-sm leading-relaxed" style={{ fontFamily: sans }}
            >
              Where Floreasca's skyline dissolves into dusk. Guan is not a venue — it is a threshold between the golden hour and the infinite night. Every surface, every sip, every silhouette is composed with obsessive intent.
            </motion.p>
            <motion.a href="#" data-mag
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              whileHover={{ letterSpacing: "0.45em", color: "#ef4444" }}
              className="inline-block mt-8 text-white/30 text-[10px] tracking-[0.35em] uppercase border-b border-red-900/40 pb-1 transition-all"
              style={{ fontFamily: sans }}
            >Enter the Experience →</motion.a>
          </motion.div>
        </div>

        {/* IMAGE — behind decorative type via z-index */}
        <div className="order-1 md:order-2 relative">
          {/* Z-behind display number */}
          <div className="absolute -left-8 top-1/2 -translate-y-1/2 z-0 pointer-events-none select-none"
            style={{ fontFamily: serif, fontSize: "clamp(8rem, 18vw, 14rem)", lineHeight: 1,
              WebkitTextStroke: "1px #991b1b22", color: "transparent", fontWeight: 900 }}>
            XII
          </div>
          <motion.div style={{ y: imgY }} className="relative z-10">
            <div className="relative h-[60vh] overflow-hidden">
              {/* Slide-up clip reveal */}
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                whileInView={{ clipPath: "inset(0% 0 0 0)" }}
                viewport={{ once: true, margin: "-8%" }}
                transition={{ duration: 1.15, ease: [0.76, 0, 0.24, 1] }}
                className="absolute inset-0"
              >
                {/* Inner scale + grayscale-to-color */}
                <motion.div
                  style={{
                    scale: imgScale,
                    filter: useTransform([imgGray, imgBright], ([g, b]) =>
                      `grayscale(${g}) brightness(${b}) saturate(${1 + (1 - g) * 0.9})`),
                  }}
                  className="w-full h-[120%] -mt-[10%]"
                >
                  <div className="w-full h-full bg-gradient-to-br from-red-950 via-stone-900 to-black relative">
                    <motion.div
                      animate={{ opacity: [0.4, 0.85, 0.4] }}
                      transition={{ duration: 6, repeat: Infinity }}
                      className="absolute inset-0"
                      style={{ background: "radial-gradient(ellipse at 60% 30%, #dc262640 0%, transparent 60%)", mixBlendMode: "screen" }}
                    />
                    <div className="absolute inset-0 flex items-end p-5">
                      <p className="text-white/20 text-[9px] tracking-[0.4em] uppercase" style={{ fontFamily: sans }}>
                        [ Terrace · Golden Hour · 19:42 ]
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
            <div className="flex justify-between items-center mt-4">
              <div className="w-12 h-px bg-red-800/50" />
              <p className="text-white/25 text-[9px] tracking-[0.4em] uppercase" style={{ fontFamily: sans }}>Floreasca Skyline</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   PARALLAX VIEW — circle reveal + skew images
───────────────────────────────────────────── */
const ParallaxView = () => {
  const trigRef = useRef(null);
  const { scrollYProgress: trigSP } = useScroll({ target: trigRef, offset: ["start 80%", "start 20%"] });
  const circleR = useSpring(useTransform(trigSP, [0, 1], [0, 140]), { stiffness: 60, damping: 20 });

  const panelRef = useRef(null);
  const { scrollYProgress: pSP } = useScroll({ target: panelRef, offset: ["start end", "end start"] });
  const y1 = useSpring(useTransform(pSP, [0, 1], [-80, 80]), SPRING);
  const y2 = useSpring(useTransform(pSP, [0, 1], [60, -60]), SPRING);
  const y3 = useSpring(useTransform(pSP, [0, 1], [-30, 90]), SPRING);

  const panels = [
    { label: "Golden Hour · 19:42", grad: "from-red-900 via-stone-900 to-black",  light: "#dc262630", span: "col-span-2 row-span-2 h-[55vh]", y: y1 },
    { label: "Midnight · 00:30",    grad: "from-stone-950 via-red-950 to-black",  light: "#7f1d1d40", span: "col-span-2 h-[27vh]",            y: y2 },
    { label: "Twilight · 21:15",    grad: "from-red-950 via-stone-950 to-black",  light: "#991b1b35", span: "col-span-2 h-[26vh]",            y: y3 },
  ];

  return (
    <section ref={trigRef} className="relative bg-black overflow-hidden py-24">
      {/* Circle clip reveal overlay */}
      <motion.div
        style={{ clipPath: useTransform(circleR, r => `circle(${r}% at 50% 50%)`) }}
        className="absolute inset-0 bg-red-950/5 pointer-events-none z-0"
      />

      <div ref={panelRef} className="relative z-10 px-8 md:px-16">
        <div className="flex justify-between items-end mb-16">
          <div>
            <p className="text-red-700/60 text-[10px] tracking-[0.55em] uppercase mb-3" style={{ fontFamily: sans }}>
              § 02 — The View
            </p>
            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "105%" }} whileInView={{ y: "0%" }}
                viewport={{ once: true }} transition={{ ...SPRING, delay: 0.1 }}
                className="font-black leading-none tracking-tight"
                style={{ fontFamily: serif, fontSize: "clamp(3rem, 8vw, 7rem)" }}
              >
                <span className="text-white">Floreasca</span><br />
                <em className="font-normal" style={{ WebkitTextStroke: "1px #991b1b", color: "transparent" }}>from above</em>
              </motion.h2>
            </div>
          </div>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="hidden md:block text-white/20 text-xs text-right max-w-[160px] leading-relaxed"
            style={{ fontFamily: sans }}
          >Level 12 above Bucharest's most storied neighbourhood</motion.p>
        </div>

        <div className="grid grid-cols-4 gap-3 relative">
          {/* Ghost text behind panels */}
          <div className="absolute -right-4 top-1/2 -translate-y-1/2 z-0 pointer-events-none select-none overflow-hidden">
            <span className="font-black leading-none"
              style={{ fontFamily: serif, fontSize: "20vw", WebkitTextStroke: "1px #991b1b15", color: "transparent" }}>
              VIEW
            </span>
          </div>

          {panels.map((p, i) => {
            const gs  = useTransform(pSP, [0, 0.45], [1, 0]);
            const br  = useTransform(pSP, [0, 0.45], [0.5, 1]);
            const sk  = useSpring(useTransform(pSP, [0, 0.5], [5, 0]), SPRING);
            return (
              <motion.div key={i} style={{ y: p.y }}
                className={`relative overflow-hidden ${p.span} z-10`}
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                whileInView={{ clipPath: "inset(0% 0 0 0)" }}
                viewport={{ once: true, margin: "-5%" }}
                transition={{ duration: 1.05, delay: i * 0.14, ease: [0.76, 0, 0.24, 1] }}
              >
                <motion.div
                  style={{
                    skewY: sk,
                    scale: useSpring(useTransform(pSP, [0, 0.5], [1.15, 1]), SPRING),
                    filter: useTransform([gs, br], ([g, b]) =>
                      `grayscale(${g}) brightness(${b}) saturate(${1 + (1 - g) * 0.8})`),
                  }}
                  className={`absolute inset-0 bg-gradient-to-br ${p.grad}`}
                >
                  <div className="absolute inset-0"
                    style={{ background: `radial-gradient(ellipse at 60% 30%, ${p.light} 0%, transparent 65%)`, mixBlendMode: "screen" }} />
                  <motion.div
                    animate={{ y: ["0%", "100%"] }}
                    transition={{ duration: 3 + i, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-x-0 h-px bg-red-600/20"
                  />
                </motion.div>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-red-600/30 to-transparent z-10" />
                <div className="absolute bottom-4 left-4 z-20">
                  <span className="text-white/30 text-[9px] tracking-[0.4em] uppercase" style={{ fontFamily: sans }}>{p.label}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   HORIZONTAL EVENTS — blood red & obsidian
───────────────────────────────────────────── */
const EVENTS = [
  { cat: "Private Party",  title: "Skyline\nSoirée",       desc: "Exclusive venue hire above Bucharest. Intimate dinners to full rooftop takeovers. An evening the city will not forget.", tag: "12 – 220 guests",     bg: "from-red-950 via-black to-black",            accent: "#7f1d1d" },
  { cat: "Mixology",       title: "The Alchemy\nBar",       desc: "Craft cocktails that mirror the skyline. Seasonal botanicals. Bespoke pairings. The bar as high ritual.",              tag: "Wed – Sun · 18:00",  bg: "from-stone-950 via-red-950/40 to-black",     accent: "#450a0a" },
  { cat: "Corporate",      title: "Elevated\nHospitality",  desc: "Impress clients above the city. Curated F&B, AV infrastructure, and a view that closes deals before you speak.",       tag: "Custom Packages",    bg: "from-black via-stone-900 to-red-950/30",     accent: "#1c0a0a" },
  { cat: "Residencies",    title: "Artist\nNights",         desc: "Monthly musical residencies. International DJs. Live sessions under open sky. Culture above the noise.",               tag: "Monthly Programme",  bg: "from-red-950/70 via-stone-950 to-black",     accent: "#5c0808" },
];

const HorizontalEvents = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useSpring(
    useTransform(scrollYProgress, [0, 1], ["0vw", `-${(EVENTS.length - 1) * 100}vw`]),
    SPRING
  );
  return (
    <section ref={ref} style={{ height: `${EVENTS.length * 100}vh` }} className="relative">
      <div className="sticky top-0 h-screen overflow-hidden">
        <div className="absolute top-8 left-8 md:left-14 z-20">
          <p className="text-red-700/50 text-[10px] tracking-[0.55em] uppercase" style={{ fontFamily: sans }}>§ 03 — Events</p>
        </div>
        <motion.div style={{ x }} className="flex h-full w-max">
          {EVENTS.map((ev, i) => (
            <div key={i}
              className={`relative flex-none w-screen h-full bg-gradient-to-br ${ev.bg} flex items-end pb-20 px-10 md:px-24 overflow-hidden`}
            >
              {/* Haze */}
              <div className="absolute inset-0 pointer-events-none">
                <motion.div
                  animate={{ opacity: [0.4, 0.85, 0.4], scale: [1, 1.1, 1] }}
                  transition={{ duration: 8 + i, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute top-0 right-0 w-3/4 h-3/4 rounded-full"
                  style={{ background: `radial-gradient(ellipse, ${ev.accent}60 0%, transparent 70%)`, mixBlendMode: "screen" }}
                />
              </div>
              {/* Ghost number — z behind content */}
              <div className="absolute top-1/2 right-12 -translate-y-1/2 select-none pointer-events-none z-0"
                style={{ fontFamily: serif, fontSize: "30vw", fontWeight: 900, lineHeight: 1,
                  WebkitTextStroke: "1px #ffffff06", color: "transparent" }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              {/* Content — z above ghost */}
              <div className="relative z-10 max-w-2xl">
                <motion.p
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: 0.1, ...SPRING }}
                  className="text-red-700/80 text-[10px] tracking-[0.55em] uppercase mb-6"
                  style={{ fontFamily: sans }}
                >{ev.cat}</motion.p>
                <h3 style={{ fontFamily: serif, whiteSpace: "pre-line", fontSize: "clamp(4rem, 8vw, 9rem)", lineHeight: 0.92 }}
                  className="text-white font-black tracking-tight mb-8">{ev.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed max-w-md mb-10" style={{ fontFamily: sans }}>
                  {ev.desc}
                </p>
                <div className="flex items-center gap-8">
                  <span className="text-white/25 text-[10px] tracking-[0.35em] uppercase border border-red-900/30 px-5 py-2.5"
                    style={{ fontFamily: sans }}>{ev.tag}</span>
                  <motion.a href="#" data-mag
                    whileHover={{ x: 8, color: "#ef4444" }} transition={SPRING}
                    className="text-white/30 text-[10px] tracking-[0.35em] uppercase flex items-center gap-2"
                    style={{ fontFamily: sans }}>Inquire <span>→</span></motion.a>
                </div>
              </div>
              <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-red-700/40 to-transparent" />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   TICKER STRIP
───────────────────────────────────────────── */
const TickerStrip = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useSpring(useTransform(scrollYProgress, [0, 1], [0, -300]), SPRING);
  return (
    <section ref={ref} className="border-y border-red-950/40 py-5 overflow-hidden bg-black">
      <motion.div style={{ x }} className="flex gap-16 whitespace-nowrap">
        {[...Array(8)].map((_, i) => (
          <span key={i} className="text-red-900/60 text-[10px] tracking-[0.5em] uppercase flex-none"
            style={{ fontFamily: sans }}>
            Reservations Open · hello@guanrooftop.ro · +40 21 000 0000 · Floreasca, Bucharest ·&nbsp;
          </span>
        ))}
      </motion.div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   MAGNETIC FOOTER
───────────────────────────────────────────── */
const MagBlock = ({ children, className }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const sx = useSpring(mx, SPRING), sy = useSpring(my, SPRING);
  const move  = (e) => { const r = ref.current.getBoundingClientRect(); mx.set((e.clientX - r.left - r.width / 2) * 0.2); my.set((e.clientY - r.top - r.height / 2) * 0.2); };
  const leave = () => { mx.set(0); my.set(0); };
  return (
    <motion.div ref={ref} style={{ x: sx, y: sy }} onMouseMove={move} onMouseLeave={leave}
      className={`cursor-pointer ${className}`} data-mag>{children}</motion.div>
  );
};

const Footer = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end end"] });
  const opacity = useTransform(scrollYProgress, [0, 0.25], [0, 1]);
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [80, 0]), SPRING);
  return (
    <footer ref={ref} className="relative bg-black pt-36 pb-14 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] rounded-full"
          style={{ background: "radial-gradient(ellipse, #7f1d1d18 0%, transparent 70%)", mixBlendMode: "screen" }} />
      </div>
      <motion.div style={{ opacity, y }} className="relative z-10 px-8 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-20 mb-28">
          <div>
            <p className="text-red-800/60 text-[10px] tracking-[0.5em] uppercase mb-8" style={{ fontFamily: sans }}>
              Reserve your night above Bucharest
            </p>
            <MagBlock className="inline-block">
              <h2 className="font-black leading-none tracking-tight text-white hover:text-red-500 transition-colors duration-700"
                style={{ fontFamily: serif, fontSize: "clamp(4rem, 10vw, 10rem)", lineHeight: 0.9 }}>
                Book a<br />
                <em className="font-normal" style={{ WebkitTextStroke: "1px #991b1b", color: "transparent" }}>Table</em>
              </h2>
            </MagBlock>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-white/50 text-xs tracking-widest uppercase" style={{ fontFamily: sans }}>Wed – Sun</p>
            <p className="text-white/25 text-xs" style={{ fontFamily: sans }}>18:00 – 02:00</p>
            <div className="w-full h-px bg-red-950/50 my-2" />
            <p className="text-white/50 text-xs tracking-widest uppercase" style={{ fontFamily: sans }}>Floreasca</p>
            <p className="text-white/25 text-xs" style={{ fontFamily: sans }}>Bucharest · Level 12</p>
            <div className="mt-4">
              <motion.a href="#" data-mag
                whileHover={{ letterSpacing: "0.45em", color: "#ef4444" }} transition={SPRING}
                className="text-red-700/70 text-[10px] tracking-[0.35em] uppercase border-b border-red-900/30 pb-1 transition-all"
                style={{ fontFamily: sans }}
              >hello@guanrooftop.ro</motion.a>
            </div>
          </div>
        </div>
        <div className="border-t border-red-950/30 pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <span style={{ fontFamily: serif }} className="text-2xl text-white/15 font-black tracking-[0.3em]">GUAN</span>
          <p className="text-white/15 text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: sans }}>
            © 2025 Guan Rooftop. All Rights Reserved.
          </p>
          <div className="flex gap-8 text-red-900/50 text-[10px] tracking-widest uppercase" style={{ fontFamily: sans }}>
            {["Instagram", "Facebook", "Press Kit"].map(s => (
              <motion.a key={s} href="#" data-mag whileHover={{ color: "#ef4444" }} transition={SPRING}>{s}</motion.a>
            ))}
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

/* ─────────────────────────────────────────────
   ROOT
───────────────────────────────────────────── */
export default function App() {
  useFonts();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <div className="bg-black min-h-screen">
      <style>{`
        *{cursor:none!important}
        ::selection{background:#7f1d1b;color:#fff}
        ::-webkit-scrollbar{width:2px}
        ::-webkit-scrollbar-track{background:#000}
        ::-webkit-scrollbar-thumb{background:#7f1d1d}
        html{scroll-behavior:auto}
      `}</style>
      <BloodGrain />
      <RedCursor />
      <Nav onMenu={() => setMenuOpen(true)} />
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      <main>
        <Hero />
        <Manifesto />
        <ParallaxView />
        <HorizontalEvents />
        <TickerStrip />
        <Footer />
      </main>
    </div>
  );
}